import { useMemo, useState, forwardRef, useEffect, useCallback } from 'react';
import type { FunctionComponent, KeyboardEvent, PropsWithoutRef, Ref } from 'react';

import Text from '../../Text';
import { CALENDAR_TYPES, type CalendarType } from '../Input/local';
import {
  getCalendarTypeFromLocale,
  getDateOfWeek,
  getWeek,
  getWeekYear,
  parseToDate,
  toUTCDateString
} from '../Input/utils';
import { useConfiguration, useConsolidatedRef, useFocusWithin, useI18n } from '../../../hooks';
import type { ForwardProps, TestIdProp } from '../../../types';
import type { FormControlProps } from '../../FormControl';
import { cap } from '../../../utils';
import VisuallyHiddenText from '../../VisuallyHiddenText';
import { getAdjustedUTCDateTime, getAdjustedUTCDateTimeForTimezone } from '../utils';

import { getDaysOfMonth, getLocalizedDays, getWeeksOfMonth } from './utils';
import {
  StyledColumnLabel,
  StyledPick,
  StyledDay,
  StyledCalendar,
  StyledWeekGrid,
  StyledWeekLabel,
  StyledWeekNumber
} from './Calendar.styles';

export interface CalendarProps extends TestIdProp {
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: FormControlProps['id'];
  /** Defines default date that will be selected initially when the Calendar is visible. */
  selected?: Date;
  /**
   * Defines currently visible month.
   * @default new Date()
   */
  current?: Date;
  /** Defines dates range with start date and end date that can be set by the user. */
  selectedRange?: { startDate: Date | undefined; endDate: Date | undefined };
  /** Defines if days should be highlighted for range selection */
  showHighlight?: boolean;
  /** Defines if week numbers should be displayed in calendar */
  showWeekNumber?: boolean;
  /**
   * The mode of the calendar that will be rendered, ie. whether single date or week are selectable.
   * @default 'date'
   */
  mode?: 'date' | 'week';
  /** Callback fired when new day is selected. */
  onSelect?: (date: Date) => void;
  /** Callback fired when user focuses new day. */
  onFocus?: (date: Date) => void;
  /** Sets the type of calendar to be used. This will be determined by locale if no value is provided. */
  calendarType?: CalendarType;
  /** Defines minimum available value that can be selected by the user. */
  min?: string | number | Date;
  /** Defines maximum available value that can be selected by the user. */
  max?: string | number | Date;
  /** Set the calendar to disabled state, ie. non-interactive. */
  disabled?: boolean;
  /** IANA timezone string. When provided, today highlight uses server timezone instead of browser timezone. */
  timeZone?: string;
}

const ariaOpts = { day: 'numeric', month: 'long', year: 'numeric' } as const;

const Calendar: FunctionComponent<CalendarProps & ForwardProps> = forwardRef(function Calendar(
  {
    testId,
    selected,
    current,
    selectedRange,
    showHighlight,
    mode = 'date',
    showWeekNumber,
    calendarType: calendar = CALENDAR_TYPES.ISO_8601,
    onSelect,
    onFocus,
    disabled,
    min: minProp,
    max: maxProp,
    timeZone
  }: PropsWithoutRef<CalendarProps>,
  ref: Ref<HTMLTableElement>
) {
  const { locale } = useConfiguration();

  const min = minProp ? parseToDate(minProp) : -Infinity;
  const max = maxProp ? parseToDate(maxProp) : Infinity;

  // Only adjust for timezone if the original prop was NOT a string
  if (min instanceof Date && typeof minProp !== 'string')
    min.setUTCMinutes(min.getUTCMinutes() - min.getTimezoneOffset());
  if (max instanceof Date && typeof maxProp !== 'string')
    max.setUTCMinutes(max.getUTCMinutes() - max.getTimezoneOffset());

  const showWeekLabel = mode === 'week' ? false : showWeekNumber;
  const effectiveCurrent = useMemo(
    () =>
      current ??
      (selected ? new Date(selected.getTime()) : null) ??
      (timeZone ? getAdjustedUTCDateTimeForTimezone(timeZone) : getAdjustedUTCDateTime()),
    [current, selected, timeZone]
  );
  const [focusedDay, setFocusedDay] = useState(effectiveCurrent);
  useEffect(() => {
    setFocusedDay(effectiveCurrent);
  }, [effectiveCurrent.getUTCFullYear(), effectiveCurrent.getUTCMonth()]);

  const calendarType = useMemo(
    () => calendar ?? getCalendarTypeFromLocale(locale),
    [locale, calendar]
  );

  const [highlightBoundary, setHighlightBoundary] = useState<Date>();

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (
        (mode === 'date'
          ? ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
          : ['ArrowUp', 'ArrowDown']
        ).includes(e.key)
      ) {
        e.preventDefault();
        const newFocused = new Date(focusedDay);
        switch (e.key) {
          case 'ArrowUp':
            newFocused.setUTCDate(focusedDay.getUTCDate() - 7);
            break;
          case 'ArrowDown':
            newFocused.setUTCDate(focusedDay.getUTCDate() + 7);
            break;
          case 'ArrowRight':
            newFocused.setUTCDate(focusedDay.getUTCDate() + 1);
            break;
          case 'ArrowLeft':
            newFocused.setUTCDate(focusedDay.getUTCDate() - 1);
            break;
          default:
        }
        if (newFocused < min || newFocused > max) return;
        setFocusedDay(newFocused);
        onFocus?.(newFocused);
        setHighlightBoundary(newFocused);
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        onSelect?.(focusedDay);
      }
    },
    [mode, focusedDay, onFocus, onSelect]
  );

  const containerRef = useConsolidatedRef<HTMLTableElement>(ref);
  const hasFocus = useFocusWithin([containerRef]);

  const today = useMemo(
    () =>
      toUTCDateString(
        timeZone ? getAdjustedUTCDateTimeForTimezone(timeZone) : getAdjustedUTCDateTime()
      ),
    [timeZone]
  );

  const isDisabled = (day: Date) => {
    return Number(min) > day.getTime() || day.getTime() > Number(max);
  };

  const isSelected = (day: Date) => {
    return [
      selected && toUTCDateString(selected),
      selectedRange?.startDate && toUTCDateString(selectedRange?.startDate),
      selectedRange?.endDate && toUTCDateString(selectedRange?.endDate)
    ].includes(toUTCDateString(day));
  };

  const isWeekSelected = (day: Date) => {
    return (
      selected &&
      toUTCDateString(day) ===
        toUTCDateString(
          getDateOfWeek(
            getWeek(selected, calendarType, 'UTC'),
            getWeekYear(selected, calendarType, 'UTC'),
            calendarType
          )
        )
    );
  };

  const isFocused = (day: Date) => {
    return toUTCDateString(day) === toUTCDateString(focusedDay);
  };

  const isWeekFocused = (day: Date) => {
    return (
      toUTCDateString(day) ===
      toUTCDateString(
        getDateOfWeek(
          getWeek(focusedDay, calendarType, 'UTC'),
          getWeekYear(focusedDay, calendarType, 'UTC'),
          calendarType
        )
      )
    );
  };

  const isInRange = (day: Date) => {
    return (
      selectedRange &&
      selectedRange.startDate &&
      selectedRange.endDate &&
      day >= selectedRange.startDate &&
      day <= selectedRange.endDate
    );
  };

  const isHighlighted = (day: Date) => {
    if (!selectedRange || !showHighlight || !highlightBoundary) return false;
    const betweenHighlightAndBoundary = (rangeBoundaryType: 'startDate' | 'endDate') => {
      const rangeBoundary = selectedRange[rangeBoundaryType];
      if (!rangeBoundary) return false;
      return !!(
        rangeBoundary &&
        ((day >= rangeBoundary && day <= highlightBoundary) ||
          (day <= rangeBoundary && day >= highlightBoundary))
      );
    };
    return betweenHighlightAndBoundary('startDate') || betweenHighlightAndBoundary('endDate');
  };

  useEffect(() => {
    if (hasFocus)
      containerRef.current?.querySelector<HTMLButtonElement>('button[tabindex="0"]')?.focus();
  }, [hasFocus, focusedDay]);

  const days = getDaysOfMonth(effectiveCurrent, calendarType ?? CALENDAR_TYPES.ISO_8601).map(
    day => {
      return (
        <StyledDay
          key={day.getTime()}
          tabIndex={isFocused(day) ? 0 : -1}
          disabled={disabled || mode === 'week' || isDisabled(day)}
          current={toUTCDateString(day) === today}
          highlighted={isInRange(day) || isHighlighted(day)}
          aria-selected={isSelected(day) || undefined}
          aria-current={isSelected(day) ? 'date' : undefined}
          dayInNexOrPrevMonth={day.getUTCMonth() !== effectiveCurrent.getUTCMonth()}
          aria-label={toUTCDateString(day, Intl.DateTimeFormat(locale, ariaOpts))}
          onMouseOver={showHighlight ? () => setHighlightBoundary(day) : undefined}
          onMouseOut={showHighlight ? () => setHighlightBoundary(undefined) : undefined}
          onClick={() => {
            setFocusedDay(day);
            onFocus?.(day);
            onSelect?.(day);
          }}
        >
          {day.getUTCDate().toLocaleString(locale)}
        </StyledDay>
      );
    }
  );

  const names = useMemo(() => getLocalizedDays(calendarType, locale), [locale, calendarType]);

  const t = useI18n();

  const displayNames = new Intl.DisplayNames(locale, {
    style: 'long',
    type: 'dateTimeField'
  }) as Intl.DateTimeDisplayNames;
  const displayNamesShort = new Intl.DisplayNames(locale, {
    style: 'short',
    type: 'dateTimeField'
  }) as Intl.DateTimeDisplayNames;

  const weeks = getWeeksOfMonth(effectiveCurrent, calendarType).map(({ weekNo, weekYear }) => {
    return (
      <>
        <Text as={StyledWeekNumber} key={weekNo} aria-hidden>
          {weekNo.toString()}
        </Text>
        <VisuallyHiddenText>
          {`${displayNames.of('weekOfYear')} ${weekNo.toString()} ${weekYear.toString()}`}
        </VisuallyHiddenText>
      </>
    );
  });

  const weeksButtons = getWeeksOfMonth(effectiveCurrent, calendarType).map(
    ({ weekNo, weekYear }) => {
      const dateOfWeek = getDateOfWeek(weekNo, weekYear, calendarType);
      const lastDayOfWeek = new Date(dateOfWeek);
      lastDayOfWeek.setDate(dateOfWeek.getDate() + 6);
      return (
        <StyledPick
          key={weekNo}
          tabIndex={isWeekFocused(dateOfWeek) ? 0 : -1}
          disabled={disabled || mode === 'date' || isDisabled(dateOfWeek)}
          aria-selected={isWeekSelected(dateOfWeek) || undefined}
          aria-label={`${displayNames.of('weekOfYear')} ${weekNo.toString()}: ${toUTCDateString(
            dateOfWeek,
            Intl.DateTimeFormat(locale, ariaOpts)
          )} - ${toUTCDateString(lastDayOfWeek, Intl.DateTimeFormat(locale, ariaOpts))}`}
          aria-current={isWeekFocused(dateOfWeek) ? 'date' : undefined}
          onMouseDown={() => {
            setFocusedDay(dateOfWeek);
            onFocus?.(dateOfWeek);
            onSelect?.(dateOfWeek);
          }}
        >
          {`${cap(displayNames.of('weekOfYear'))} `}
          {weekNo.toLocaleString(locale)}
        </StyledPick>
      );
    }
  );

  const weekLabel =
    mode === 'date' ? (
      <>
        <Text
          as={StyledWeekLabel}
          forwardedAs='abbr'
          title={cap(displayNames.of('weekOfYear'))}
          aria-hidden
        >
          {cap(displayNamesShort.of('weekOfYear'))}
        </Text>
        <VisuallyHiddenText>{displayNames.of('weekOfYear')}</VisuallyHiddenText>
      </>
    ) : (
      <Text as={StyledWeekLabel}>{cap(displayNames.of('weekOfYear'))}</Text>
    );

  const headerLabels = names.map(([name, abbr]) => (
    <th role='columnheader' key={name}>
      <Text as={StyledColumnLabel} forwardedAs='abbr' key={name} title={name} aria-hidden>
        {abbr}
      </Text>
      <VisuallyHiddenText>{name}</VisuallyHiddenText>
    </th>
  ));

  const splitToWeeks = (resultArray: JSX.Element[][], item: JSX.Element, index: number) => {
    const chunkIndex = Math.floor(index / 7);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [] as JSX.Element[];
    }

    resultArray[chunkIndex].push(item);
    return resultArray;
  };

  const cells = days.reduce(splitToWeeks, []).map((week, index) => {
    const rowHeader = mode === 'week' ? weeksButtons[index] : weeks[index];
    return (
      // eslint-disable-next-line react/no-array-index-key
      <tr key={`week-${index}`}>
        {showWeekLabel && <th scope='row'>{rowHeader}</th>}
        {week.map((day, dayIndex) => {
          // eslint-disable-next-line react/no-array-index-key
          return <td key={`day-${dayIndex}`}>{day}</td>;
        })}
      </tr>
    );
  });

  const weeksCells = weeksButtons.map((week, index) => {
    return (
      // eslint-disable-next-line react/no-array-index-key
      <tr key={`week-${index}`}>
        <td>{week}</td>
      </tr>
    );
  });

  const weeksGrid = (
    <StyledWeekGrid role='grid' onKeyDown={onKeyDown} ref={containerRef}>
      <thead>
        <tr>
          <th>{weekLabel}</th>
        </tr>
      </thead>
      <tbody>{weeksCells}</tbody>
    </StyledWeekGrid>
  );

  return (
    <>
      {mode === 'week' && weeksGrid}
      <StyledCalendar
        data-testid={testId}
        ref={mode === 'date' ? containerRef : undefined}
        aria-label={effectiveCurrent.toLocaleString(locale, {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC'
        })}
        onKeyDown={onKeyDown}
        aria-hidden={mode === 'week' ? true : undefined}
        role='grid'
      >
        <thead>
          <tr>
            {showWeekLabel && <th>{weekLabel}</th>}
            {headerLabels}
          </tr>
        </thead>
        <tbody>{cells}</tbody>
      </StyledCalendar>
      <VisuallyHiddenText aria-live='polite' role='alert'>
        {t('calendar_assist')}
      </VisuallyHiddenText>
    </>
  );
});

export default Calendar;
